import { Box, Checkbox, InternalStandardProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { Layout } from "../Layout";
import { AppHeader } from "JS/React/Components/AppHeader";
import { AppTypography } from "JS/React/Components/AppTypography";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useRouting } from "JS/React/Hooks/Routes";
import { useChecklist } from "JS/React/Hooks/Checklist";
import { messaging } from "JS/Helpers/UserMessaging";
import { ChecklistText } from "./ChecklistText";

export interface ChecklistProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

const StyledRoot = styled("div")(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(0, 2, 3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
}));

const StyledBanner = styled("img")({
    width: "100%",
    aspectRatio: "2 / 1",
    height: "auto",
    display: "block",
});

const StyledBody = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey.A100,
}));

const StyledCentered = styled(StyledBody)({
    textAlign: "center",
});

const StyledIndicatorsHeading = styled(StyledBody)({
    fontSize: "1.15rem",
});

const StyledStateMessage = styled(StyledBody)(({ theme }) => ({
    textAlign: "center",
    padding: theme.spacing(3, 2),
}));

const StyledIndicators = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
}));

const StyledItemRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
    padding: theme.spacing(0.75),
    borderRadius: 0,
    "&.Mui-disabled": {
        color: theme.palette.primary.main,
    },
}));

const StyledItemText = styled(StyledBody)(({ theme }) => ({
    paddingTop: theme.spacing(1),
}));

const StyledFinePrint = styled(StyledBody)({
    lineHeight: 1.5,
    display: "block",
});

const StyledFinePrintList = styled("ul")(({ theme }) => ({
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
}));

export const Checklist = (props: ChecklistProps) => {
    const { className, ...rest } = props;
    const { linkProvider } = useRouting();
    const navigate = useNavigate();
    const { items, pendingItems, bannerUrl, loading, loadFailed, checkItem } =
        useChecklist();

    return (
        <Layout>
            <AppHeader
                title={messaging.checklist.title}
                canGoBack
                searchable={false}
                onBackClick={() => navigate(linkProvider.react.home())}
            />

            {loading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={messaging.checklist.loading}
                />
            )}

            <StyledRoot {...rest} className={className}>
                <StyledBanner
                    src={bannerUrl}
                    alt={messaging.checklist.bannerAlt}
                />

                <StyledBody variant="body1">
                    <ChecklistText segments={messaging.checklist.goal} />
                </StyledBody>

                <StyledBody variant="body1">
                    <ChecklistText segments={messaging.checklist.deadline} />
                </StyledBody>

                <StyledBody variant="body1">
                    <ChecklistText segments={messaging.checklist.reward} />
                </StyledBody>

                <StyledCentered variant="body1">
                    <ChecklistText segments={messaging.checklist.honorSystem} />
                </StyledCentered>

                <StyledIndicatorsHeading variant="body1">
                    <ChecklistText
                        segments={messaging.checklist.indicatorsHeading}
                    />
                </StyledIndicatorsHeading>

                <StyledBody variant="body1">
                    <ChecklistText segments={messaging.checklist.returnNote} />
                </StyledBody>

                {!loading && loadFailed && (
                    <StyledStateMessage variant="body2">
                        {messaging.checklist.loadFailed}
                    </StyledStateMessage>
                )}

                {!loading && !loadFailed && items.length === 0 && (
                    <StyledStateMessage variant="body2">
                        {messaging.common.noItemsFound}
                    </StyledStateMessage>
                )}

                <StyledIndicators>
                    {!loadFailed &&
                        items.map((item) => (
                            <StyledItemRow key={item.itemNumber}>
                                <StyledCheckbox
                                    checked={item.isChecked}
                                    disabled={
                                        item.isChecked ||
                                        pendingItems.includes(item.itemNumber)
                                    }
                                    onChange={() => checkItem(item.itemNumber)}
                                    slotProps={{
                                        input: { "aria-label": item.itemText },
                                    }}
                                />
                                <StyledItemText variant="body1">
                                    {item.itemText}
                                </StyledItemText>
                            </StyledItemRow>
                        ))}
                </StyledIndicators>

                <Box>
                    <StyledFinePrint variant="body2">
                        <ChecklistText
                            segments={messaging.checklist.finePrintIntro}
                        />
                    </StyledFinePrint>
                    <StyledFinePrintList>
                        {messaging.checklist.finePrint.map((line, index) => (
                            <li key={index}>
                                <StyledFinePrint variant="body2">
                                    <ChecklistText segments={line} />
                                </StyledFinePrint>
                            </li>
                        ))}
                    </StyledFinePrintList>
                </Box>
            </StyledRoot>
        </Layout>
    );
};
